import * as React from 'react';
import { useApi, githubAuthApiRef, configApiRef } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useDebounce from 'react-use/esm/useDebounce';
import { FieldValidation } from '@rjsf/utils';

export type RepoSetupState = {
  collaboratorGitHubUsername: string;
  collaboratorRole: string;
  repoUrl?: string;
  customRepoName?: string;
};

const NOT_CONNECTED = '__not_connected__';
const DEFAULT_ROLES = ['admin', 'maintain', 'write', 'triage', 'read'];

function parseRepoSetupState(formData: RepoSetupState | undefined): RepoSetupState {
  return {
    collaboratorGitHubUsername: formData?.collaboratorGitHubUsername ?? '',
    collaboratorRole: formData?.collaboratorRole ?? 'admin',
    customRepoName: formData?.customRepoName,
  };
}

export const validateRepoSetup = (
  value: RepoSetupState,
  validation: FieldValidation,
) => {
  if (!value?.collaboratorGitHubUsername || value.collaboratorGitHubUsername === NOT_CONNECTED) {
    validation.addError('Connect GitHub in Settings → Auth Providers');
  }
};

/**
 * The underlying component that is rendered in the form for the `CustomRepoSetupField`
 * field extension.
 *
 * @public
 */
export const CustomRepoSetupField = (
  props: FieldExtensionComponentProps<RepoSetupState>,
) => {
  const { uiSchema, onChange, rawErrors, formData, schema, formContext } = props;
  const [state, setState] = useState<RepoSetupState>(
    parseRepoSetupState(formData),
  );
  const [credentialsHost, setCredentialsHost] = useState<string | undefined>(
    undefined,
  );

  const githubAuth = useApi(githubAuthApiRef);
  const configApi = useApi(configApiRef);
  const githubHost = configApi.getOptionalString('app.github.host') ?? 'ascentt-systems-inc.ghe.com';
  const githubOrg = configApi.getOptionalString('app.github.org') ?? 'Ascentt-Systems';

  const repoName = (formContext as any)?.formData?.name ?? '';

  const allowedRoles = useMemo(
    () => (uiSchema?.['ui:options'] as any)?.allowedRoles ?? DEFAULT_ROLES,
    [uiSchema],
  );
  const isDisabled = useMemo(
    () => (uiSchema?.['ui:disabled'] as boolean) ?? false,
    [uiSchema],
  );
  const { collaboratorRole } = state;

  const allowCustomRepoName = useMemo(
    () => (uiSchema?.['ui:allowCustomRepoName'] as boolean) ?? false,
    [uiSchema],
  );

  const currentRepoName = allowCustomRepoName ? (state.customRepoName ?? repoName) : repoName;

  useEffect(() => {
    onChange({
      ...state,
      repoUrl: `${githubHost}?owner=${githubOrg}&repo=${currentRepoName}`
    });
  }, [state, currentRepoName, onChange, githubHost, githubOrg]);

  /* we deal with calling the role setting here instead of in each component for ease */
  useEffect(() => {
    if (allowedRoles.length > 0 && !collaboratorRole) {
      setState(prevState => ({
        ...prevState,
        collaboratorRole: allowedRoles[0],
      }));
    }
  }, [setState, allowedRoles, collaboratorRole]);

  const updateLocalState = useCallback(
    (newState: Partial<RepoSetupState>) => {
      setState(prevState => ({ ...prevState, ...newState }));
    },
    [setState],
  );

  useDebounce(
    async () => {
      // don't show login prompt if credentials are already fetched
      if (credentialsHost !== undefined) {
        return;
      }

      try {
        // Try identity from existing session first (no popup)
        const identity = await githubAuth.getBackstageIdentity({ optional: true });
        if (identity?.identity?.userEntityRef) {
          const login = identity.identity.userEntityRef.split('/').pop() ?? '';
          if (login) {
            updateLocalState({ collaboratorGitHubUsername: login });
            setCredentialsHost(githubHost);
            return;
          }
        }

        // Fallback: OAuth access token → GitHub API
        const token = await githubAuth.getAccessToken(['read:user'], {
          optional: true,
          instantPopup: false,
        });

        if (!token) {
          updateLocalState({ collaboratorGitHubUsername: NOT_CONNECTED });
          setCredentialsHost('');
          return;
        }

        const res = await fetch(`https://${githubHost}/api/v3/user`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          updateLocalState({ collaboratorGitHubUsername: NOT_CONNECTED });
          setCredentialsHost('');
          return;
        }

        const data = await res.json();
        if (data.login) {
          updateLocalState({ collaboratorGitHubUsername: data.login });
          setCredentialsHost(githubHost);
        }
      } catch {
        updateLocalState({ collaboratorGitHubUsername: NOT_CONNECTED });
        setCredentialsHost('');
      }
    },
    500,
    [state, uiSchema],
  );

  const description = uiSchema?.['ui:description'] ?? schema.description;

  return (
    <>
      <Box my={1} pb={1}>
        <Typography variant="h5">Repository Location</Typography>
        <Divider />
      </Box>
      {description && (
        <Typography variant="body1">{description}</Typography>
      )}

      <Box pb={4}>
        <FormControl fullWidth>
          <InputLabel shrink>Host</InputLabel>
          <Select value={githubHost} disabled>
            <MenuItem value={githubHost}>{githubHost}</MenuItem>
          </Select>
          <FormHelperText>
            The host where the repository will be created
          </FormHelperText>
        </FormControl>
      </Box>

      <Box pb={4}>
        <FormControl fullWidth>
          <InputLabel shrink>Owner Available</InputLabel>
          <Select value={githubOrg} disabled>
            <MenuItem value={githubOrg}>{githubOrg}</MenuItem>
          </Select>
          <FormHelperText>
            The organization, user or project that this repo will belong to
          </FormHelperText>
        </FormControl>
      </Box>

      <Box pb={4}>
        <TextField
          fullWidth
          label="Repository *"
          value={currentRepoName}
          disabled={!allowCustomRepoName}
          onChange={allowCustomRepoName ? (e) => updateLocalState({ customRepoName: e.target.value }) : undefined}
          InputLabelProps={{ shrink: true }}
          helperText="The name of the repository"
        />
      </Box>

      <Box pb={4}>
        {credentialsHost === undefined && (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="textSecondary">
              Loading GitHub username...
            </Typography>
          </Box>
        )}
        {credentialsHost === '' && !(rawErrors && rawErrors.length > 0) && (
          <Typography variant="body2" style={{ color: 'red' }}>
            Connect GitHub in Settings → Auth Providers
          </Typography>
        )}
        {credentialsHost === githubHost && (
          <TextField
            fullWidth
            label="Collaborator GitHub Username *"
            value={state.collaboratorGitHubUsername}
            disabled
            InputLabelProps={{ shrink: true }}
            helperText="GitHub username of the collaborator to add."
          />
        )}
      </Box>

      {credentialsHost === githubHost && (
        <Box pb={4}>
          <FormControl fullWidth>
            <InputLabel shrink>Collaborator Role *</InputLabel>
            <Select
              value={state.collaboratorRole}
              disabled={isDisabled}
              onChange={e =>
                updateLocalState({ collaboratorRole: e.target.value as string })
              }
            >
              {allowedRoles.map((role: string) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Permission level for the collaborator.
            </FormHelperText>
          </FormControl>
        </Box>
      )}
    </>
  );
};
